import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getMyClientOnboardings } from "../services/onboardingApiService";
import { useOnboardingMode } from "../hooks/useOnboardingMode";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { ERROR_CONTEXTS } from "../utils/errorMessages";
import {
  FlexColumn,
  FlexRow,
  MessageBox,
  THEME,
} from "../components/OnboardingLayoutComponents";
import { OnboardingButtonPrimary } from "../components/OnboardingButton";
import { HeadingLarge, LabelMedium } from "../components/OnboardingTypography";
import ClientOnboardingList from "../components/ClientOnboardingList";

/**
 * ClientManagementContainer
 *
 * Container component for managing client onboardings
 * Only accessible by accountants (SUPERVISOR, USERADMIN, CUSTOMERADMIN)
 */
const ClientManagementContainer = () => {
  const [clientOnboardings, setClientOnboardings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { isBehalfMode } = useOnboardingMode();
  const { handleError, clearError, getMessageBoxProps } = useErrorHandler(
    ERROR_CONTEXTS.GENERAL
  );

  // Only accountants should access this page
  useEffect(() => {
    if (!isBehalfMode) {
      navigate("/Overview");
    }
  }, [isBehalfMode, navigate]);

  useEffect(() => {
    const loadClientOnboardings = async () => {
      setIsLoading(true);
      clearError();

      try {
        const response = await getMyClientOnboardings();
        setClientOnboardings(response.data);
      } catch (error) {
        handleError(error, ERROR_CONTEXTS.GENERAL);
      } finally {
        setIsLoading(false);
      }
    };

    if (isBehalfMode) {
      loadClientOnboardings();
    }
  }, [isBehalfMode, handleError, clearError]);

  const handleCreateNewClient = () => {
    navigate("/onboarding"); // Role-based mode detection handles the rest
  };

  const handleContinueOnboarding = (submissionId) => {
    navigate(`/onboarding/setup?submissionId=${submissionId}`);
  };

  if (!isBehalfMode) {
    return null; // Component will redirect, but this prevents flash
  }

  return (
    <FlexColumn
      alignItems="center"
      maxWidth="960px"
      margin="0 auto"
      padding="0 20px"
    >
      <FlexColumn width="100%" gap={THEME.spacing.xl}>
        <FlexRow
          justifyContent="space-between"
          alignItems="baseline"
          wrap="wrap"
          gap={THEME.spacing.md}
          padding={`${THEME.spacing.ultra} 0 0 0`}
        >
          <HeadingLarge>Kunderegistreringer</HeadingLarge>
          <OnboardingButtonPrimary
            onClick={handleCreateNewClient}
            disabled={isLoading}
          >
            <LabelMedium>Opprett ny kunde</LabelMedium>
          </OnboardingButtonPrimary>
        </FlexRow>

        {getMessageBoxProps() && <MessageBox {...getMessageBoxProps()} />}

        <ClientOnboardingList
          onboardings={clientOnboardings}
          isLoading={isLoading}
          onContinue={handleContinueOnboarding}
        />
      </FlexColumn>
    </FlexColumn>
  );
};

export default ClientManagementContainer;
